import { LineCopy } from '@qr-front/icons/line-copy'
import { LineDownload } from '@qr-front/icons/line-download'
import { LineEye } from '@qr-front/icons/line-eye'
import { LineMoreHorizontal } from '@qr-front/icons/line-more-horizontal'
import { Button } from '@qr-front/ui/components/Button'
import { Tooltip } from '@qr-front/ui/components/Tooltip'
import { cn } from '@qr-front/ui/utils'
import { showNotification } from 'components/Notification'
import { ROUTES } from 'constants/routesConstants'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { ReactNode, useCallback, useRef } from 'react'

import { createQrImageBuilder, TQrDesign } from '@/generator/client'
import { Link, useRouter } from '@/i18n-routing'
import { copyToClipboard } from '@/utils'

import { useFormatDate } from '../../../../../../hooks/dateFormattingHooks'
import { removeUrlProtocol } from '../../../../../../utils/urlUtils'
import {
	buildQrLandingUrl,
	getQrDataToEncode,
} from '../../../../../generator/utils/buildQrLandingUrl'
import {
	createdColDef,
	editDateColDef,
	previewColDef,
	scansColDef,
	selectColDef,
} from '../columnsConfig'
import {
	desktopTableRowActionPopoverAtom,
	desktopTableRowActionPopoverTypeAtom,
	desktopTableRowPopoverStateAtom,
	qrItemState,
	scanQrDialogAtom,
	scanQrDialogDataAtom,
} from '../dialogsState'
import { TABLE_ROW_ACTION_TYPE } from '../TableActions/Actions/actionOptions'
import { withExpiredDialogIfNotAllowed } from '../TableActions/Actions/tableActionUtils'
import { ShareButton } from '../TableActions/Buttons/ShareButton'
import { isScansDataStaleAtom } from '../tableState'
import { useCellValue } from '../utils/cellValue'
import { useRenderDisplayCell } from '../utils/renderDisplayCell'
import { CreateDate, Name, Status, Type, WebsiteUrl } from './commonElements'
import { PreviewCell } from './PreviewCell'

const TableCell = ({
	children,
	className,
}: {
	children: ReactNode
	className?: string
}) => (
	<td
		className={cn(
			'relative py-4 after:absolute after:right-0 after:top-1/2 after:h-[88px] after:w-px after:-translate-y-1/2 after:bg-border-grey desktop:pl-6 desktop:pr-[calc(theme(spacing.6)+1px)] large-desktop:pl-16 large-desktop:pr-[calc(theme(spacing.16)+1px)] [&:first-child]:rounded-l-10 [&:last-child]:rounded-r-10 [&:last-child]:after:hidden',
			className,
		)}
	>
		{children}
	</td>
)

const LandingUrl = ({ className }: { className?: string } = {}) => {
	const t = useTranslations()
	const setOpenScanQrModal = useSetAtom(scanQrDialogAtom)
	const setScanQrModalData = useSetAtom(scanQrDialogDataAtom)

	const { id, qrDesign, content, uri, disabled } = useCellValue(previewColDef)
	const landingUrl = buildQrLandingUrl(uri)

	const onCopy = useCallback(
		async (url: string) => {
			if (!url) return

			if (!(await copyToClipboard(url))) {
				showNotification(t('public__generic__unexpected_error'), {
					type: 'error',
					toastId: `copy-error-${url}`,
				})
				return
			}

			showNotification(
				t('generator__share_qr_dialog__success_copy_button'),
				{
					type: 'success',
					toastId: url,
				},
			)
		},
		[t],
	)

	const handleOpenScanQrModal = useCallback(async () => {
		if (!qrDesign || !content || !uri) return

		const dataToEncode = getQrDataToEncode({
			qrUri: uri,
			qrContent: content,
		})
		const qrImageBuilder = await createQrImageBuilder({
			...qrDesign,
			data: dataToEncode,
		} as Parameters<typeof createQrImageBuilder>[0])
		const previewSvgString = await qrImageBuilder.getSvg({
			disablePaddingOnDefaultFrameIfNoBg: true,
		})

		setScanQrModalData({ id, uri, previewSvgString, disabled, content })
		setOpenScanQrModal(true)
	}, [
		id,
		qrDesign,
		content,
		uri,
		disabled,
		setScanQrModalData,
		setOpenScanQrModal,
	])

	return (
		<div className="type-body-small flex min-w-0 items-center gap-1 overflow-hidden text-dark-grey">
			<button className="shrink-0" onClick={handleOpenScanQrModal}>
				<LineEye className="size-4" />
			</button>
			<a
				href={landingUrl}
				target="_blank"
				rel="noreferrer"
				className={cn('min-w-0 truncate', className)}
			>
				{removeUrlProtocol(landingUrl)}
			</a>
			<button className="shrink-0" onClick={() => onCopy(landingUrl)}>
				<LineCopy className="size-4 text-grey" />
			</button>
		</div>
	)
}

const Scans = () => {
	const t = useTranslations()
	const router = useRouter()

	const qrItem = useCellValue(previewColDef)
	const scans = useCellValue(scansColDef)
	const isDataStale = useAtomValue(isScansDataStaleAtom)

	const containerRef = useRef<HTMLDivElement>(null)

	return (
		<TableCell className="type-body-small min-w-16 text-grey">
			<Tooltip
				anchorRef={containerRef}
				delay={100}
				position="top"
				visual="private"
			>
				{t('dashboard__qr_table__qr_card__click_to_view_analytics')}
			</Tooltip>
			<div
				ref={containerRef}
				className="flex cursor-pointer flex-col items-center"
			>
				<Link
					className="flex flex-col items-center"
					href={`${ROUTES.cabinet.analytics}?id=${qrItem.id}`}
					prefetch={false}
					onMouseEnter={() =>
						router.prefetch(
							`${ROUTES.cabinet.analytics}?id=${qrItem.id}`,
						)
					}
				>
					{t.rich('dashboard__analytics__heatmap_card__scans', {
						count: scans,
						value: (chunks) => (
							<span
								className={cn(
									'type-heading-3 transition-[colors,opacity]',
									isDataStale
										? 'animate-pulse text-grey'
										: 'text-black',
								)}
							>
								{chunks}
							</span>
						),
					})}
				</Link>
			</div>
		</TableCell>
	)
}

const MainDetails = () => {
	const selectCheckbox = useRenderDisplayCell(selectColDef)

	const { id, updatedAt, qrDesign, content, uri, disabled } =
		useCellValue(previewColDef)
	const isWifiQrType = content?.type === QR_TYPE.Wifi

	return (
		<TableCell className="desktop:pl-4 large-desktop:pl-4">
			<div className="flex min-w-0 items-center gap-4">
				<div className="flex-shrink-0">{selectCheckbox}</div>
				<div className="flex-shrink-0">
					<PreviewCell
						id={id}
						updatedAt={updatedAt}
						qrDesign={qrDesign as TQrDesign}
						content={content}
						uri={uri}
						disabled={disabled}
					/>
				</div>
				<div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
					<Name className="min-w-0 truncate" />
					{!isWifiQrType && (
						<LandingUrl className="min-w-0 truncate" />
					)}
					<div className="min-w-0 truncate">
						<Type />
					</div>
					<div className="min-w-0 overflow-hidden">
						<WebsiteUrl className="min-w-0 truncate" />
					</div>
				</div>
			</div>
		</TableCell>
	)
}

export const EditDate = () => {
	const t = useTranslations()

	const date = useCellValue(editDateColDef)
	const formattedDate = useFormatDate()(date)

	return (
		<span className="type-body-small text-grey">
			{t('dashboard__qr_table__qr_card__edit_date', {
				date: formattedDate,
			})}
		</span>
	)
}

const isMoreThan7DaysAgo = (date: Date) => {
	const now = new Date()
	const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
	return date < sevenDaysAgo
}

export const TimeDetails = () => {
	const t = useTranslations()
	const created = useCellValue(createdColDef)
	const editDate = useCellValue(editDateColDef)
	const createdTime = new Date(created as string | Date).getTime()
	const editedTime = new Date(editDate as string | Date).getTime()
	const timeDifferenceInMinutes =
		Math.abs(editedTime - createdTime) / (1000 * 60)

	const isRecentlyModified =
		timeDifferenceInMinutes > 1 &&
		!isMoreThan7DaysAgo(new Date(editDate as string | Date))

	const recentlyModified = isRecentlyModified ? (
		<span className="type-body-caption text-grey">
			{t('dashboard__qr_table__qr_card__recently_modified')}
		</span>
	) : null

	return (
		<TableCell>
			<div className="flex min-w-0 shrink-0 flex-col items-start gap-1 overflow-hidden">
				<CreateDate />
				<EditDate />
				{recentlyModified}
			</div>
		</TableCell>
	)
}

const Actions = () => {
	const qrItem = useCellValue(previewColDef)

	const setQrItem = useSetAtom(qrItemState)
	const [tableRowPopoverState, setTableRowPopoverState] = useAtom(
		desktopTableRowActionPopoverAtom,
	)
	const setTableRowActionDialogTypeState = useSetAtom(
		desktopTableRowActionPopoverTypeAtom,
	)
	const setPopoverState = useSetAtom(desktopTableRowPopoverStateAtom)
	const buttonDownloadRef = useRef<HTMLButtonElement | null>(null)
	const buttonMoreRef = useRef<HTMLButtonElement | null>(null)

	const handleOpenPopover = (
		e: React.MouseEvent,
		type: TABLE_ROW_ACTION_TYPE,
	) => {
		const isDownloadType = type === TABLE_ROW_ACTION_TYPE.Download
		const isMoreType = type === TABLE_ROW_ACTION_TYPE.More

		let buttonRef: React.RefObject<HTMLButtonElement | null> | null = null

		if (isDownloadType) {
			buttonRef = buttonDownloadRef
		} else if (isMoreType) {
			buttonRef = buttonMoreRef
		}

		if (buttonRef) {
			setPopoverState({
				buttonRef,
			})
		}
	}

	const togglePopover = (
		e: React.MouseEvent,
		type: TABLE_ROW_ACTION_TYPE,
	) => {
		if (tableRowPopoverState) {
			setTableRowPopoverState(false)
		} else {
			setQrItem(qrItem)
			setTableRowPopoverState(true)
			setTableRowActionDialogTypeState(type)
			handleOpenPopover(e, type)
		}
	}

	return (
		<TableCell className="desktop:pr-4 large-desktop:pr-4">
			<div className="flex items-center justify-end gap-4">
				<div className="flex gap-2">
					<div className="flex items-center gap-2">
						<ShareButton uri={qrItem.uri} />
					</div>
					<Button
						visual="soft-shadow"
						ref={buttonDownloadRef}
						onClick={(e) =>
							withExpiredDialogIfNotAllowed(() =>
								togglePopover(
									e,
									TABLE_ROW_ACTION_TYPE.Download,
								),
							)()
						}
					>
						<LineDownload className="size-4" />
					</Button>
				</div>
				<Button
					ref={buttonMoreRef}
					visual="text"
					className="-mx-2 h-10 has-[>svg]:px-2"
					onClick={(e) =>
						togglePopover(e, TABLE_ROW_ACTION_TYPE.More)
					}
				>
					<LineMoreHorizontal />
				</Button>
			</div>
		</TableCell>
	)
}

export const TableRowDesktop = () => {
	return (
		<>
			<MainDetails />
			<Scans />
			<TimeDetails />
			<TableCell>
				<Status className="w-full min-w-[100px] justify-center" />
			</TableCell>
			<Actions />
		</>
	)
}
